import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  Alert,
  LinearProgress,
  InputAdornment,
  Tooltip,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Block,
  CheckCircle,
  Person,
  Email,
  Phone,
  Search,
  Refresh,
  Sync,
  VpnKey,
  Security,
  AdminPanelSettings,
  Badge,
  PersonOff,
  LockReset,
  VerifiedUser,
  HourglassEmpty,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { userService, partnerService } from '../services/api';
import { User, UserRole, UserStatus, UserStatistics, CreateUserRequest, UpdateUserRequest } from '../types/user';
import { getErrorMessage } from '../utils/errorUtils';
import ConfirmDialog, { DialogType } from '../components/ConfirmDialog';
import NotificationSnackbar, { NotificationType } from '../components/NotificationSnackbar';

const PARTNER_ROLES: UserRole[] = ['MERCHANT', 'MERCHANT_ADMIN', 'RETAILER', 'RETAILER_ADMIN', 'AGENT'];

interface PartnerOption {
  id: string;
  partnerCode: string;
  partnerName: string;
  partnerType: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<UserRole | ''>('');
  const [filterStatus, setFilterStatus] = useState<UserStatus | ''>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);

  // Dialog and notification states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'confirm' as DialogType,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    inputLabel: '',
    inputPlaceholder: '',
    inputRequired: false,
    inputMultiline: false,
    onConfirm: (inputValue?: string) => {},
  });

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'success' as NotificationType,
  });

  // Form state
  const [formData, setFormData] = useState<CreateUserRequest>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    roles: [],
    partnerCode: '',
    sendWelcomeEmail: true,
  });

  useEffect(() => {
    fetchUsers();
    fetchPartners();
    fetchStatistics();
  }, [page, rowsPerPage, filterRole, filterStatus]);

  const fetchStatistics = async () => {
    try {
      const stats = await userService.getUserStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to fetch user statistics:', error);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await partnerService.getPartners({ size: 500 });
      const items = (response.content || []).map((p: any) => ({
        id: p.id?.toString() || '',
        partnerCode: p.partnerCode || '',
        partnerName: p.partnerName || p.name || '',
        partnerType: p.partnerType || '',
      }));
      setPartners(items);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        size: rowsPerPage,
      };

      if (searchTerm) params.search = searchTerm;
      if (filterRole) params.role = filterRole;
      if (filterStatus) params.status = filterStatus;

      const response = await userService.getUsers(params);
      setUsers(response.content);
      setTotalElements(response.totalElements);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      const errorMessage = getErrorMessage(error, 'Failed to fetch users');
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromKeycloak = async () => {
    setSyncing(true);
    try {
      const result = await userService.syncFromKeycloak();
      const summary = `Sync complete: ${result.created} created, ${result.updated} updated, ${result.failed} failed`;
      showNotification(summary, result.failed > 0 ? 'warning' : 'success');
      fetchUsers();
      fetchStatistics();
    } catch (error) {
      console.error('Failed to sync from Keycloak:', error);
      const errorMessage = getErrorMessage(error, 'Failed to sync users from Keycloak');
      showNotification(errorMessage, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const showNotification = (message: string, type: NotificationType = 'success') => {
    setNotification({ open: true, message, type });
  };

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: (inputValue?: string) => void,
    options: Partial<typeof confirmDialog> = {}
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      type: 'confirm',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      inputLabel: '',
      inputPlaceholder: '',
      inputRequired: false,
      inputMultiline: false,
      onConfirm,
      ...options,
    });
  };

  const handleCreateUser = () => {
    setEditMode(false);
    setFormData({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      roles: [],
      partnerCode: '',
      sendWelcomeEmail: true,
    });
    setDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditMode(true);
    setSelectedUser(user);
    // Look up partner code from partnerId
    const partner = partners.find(p => p.id === user.partnerId);
    setFormData({
      username: user.username,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber || '',
      roles: user.roles,
      partnerCode: partner?.partnerCode || '',
      sendWelcomeEmail: false,
    });
    setDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      if (editMode && selectedUser) {
        const updateData: UpdateUserRequest = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber,
          roles: formData.roles,
        };
        await userService.updateUser(selectedUser.id, updateData);
        showNotification('User updated successfully', 'success');
      } else {
        await userService.createUser(formData);
        showNotification('User created successfully', 'success');
      }
      setDialogOpen(false);
      fetchUsers();
      fetchStatistics();
    } catch (error) {
      console.error('Failed to save user:', error);
      const errorMessage = getErrorMessage(error, 'Failed to save user. Please try again.');
      showNotification(errorMessage, 'error');
    }
  };

  const handleDeleteUser = (user: User) => {
    showConfirmDialog(
      'Delete User',
      `Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`,
      async () => {
        try {
          await userService.deleteUser(user.id);
          showNotification(`User ${user.username} has been deleted.`, 'success');
          fetchUsers();
          fetchStatistics();
        } catch (error) {
          console.error('Failed to delete user:', error);
          const errorMessage = getErrorMessage(error, 'Failed to delete user. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      {
        type: 'danger',
        confirmText: 'Delete',
      }
    );
  };

  const handleSuspendUser = (user: User) => {
    if (user.status === 'ACTIVE') {
      showConfirmDialog(
        'Suspend User',
        `Are you sure you want to suspend ${user.firstName} ${user.lastName}? They will not be able to access the system until reactivated.`,
        async (reason) => {
          try {
            await userService.suspendUser(user.id, reason!);
            showNotification(`User ${user.username} has been suspended.`, 'warning');
            fetchUsers();
            fetchStatistics();
          } catch (error) {
            console.error('Failed to suspend user:', error);
            const errorMessage = getErrorMessage(error, 'Failed to suspend user. Please try again.');
            showNotification(errorMessage, 'error');
          }
        },
        {
          type: 'input',
          inputLabel: 'Suspension Reason',
          inputPlaceholder: 'Please provide a reason for suspension...',
          inputRequired: true,
          confirmText: 'Suspend',
        }
      );
    } else {
      showConfirmDialog(
        'Reactivate User',
        `Are you sure you want to reactivate ${user.firstName} ${user.lastName}?`,
        async () => {
          try {
            await userService.reactivateUser(user.id);
            showNotification(`User ${user.username} has been reactivated.`, 'success');
            fetchUsers();
            fetchStatistics();
          } catch (error) {
            console.error('Failed to reactivate user:', error);
            const errorMessage = getErrorMessage(error, 'Failed to reactivate user. Please try again.');
            showNotification(errorMessage, 'error');
          }
        },
        {
          type: 'warning',
          confirmText: 'Reactivate',
        }
      );
    }
  };

  const handleResetPassword = (user: User) => {
    showConfirmDialog(
      'Reset Password',
      `Reset password for ${user.firstName} ${user.lastName}? A temporary password will be sent to ${user.email}.`,
      async () => {
        try {
          await userService.resetUserPassword({ userId: user.id, sendEmail: true });
          showNotification(`Password reset email sent to ${user.email}`, 'success');
        } catch (error) {
          console.error('Failed to reset password:', error);
          const errorMessage = getErrorMessage(error, 'Failed to reset password. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      {
        type: 'info',
        confirmText: 'Reset Password',
      }
    );
  };

  const handleUpdateRoles = (user: User) => {
    setSelectedUser(user);
    setFormData({
      ...formData,
      roles: user.roles,
    });
    // Open a custom dialog for role management
    showConfirmDialog(
      'Update User Roles',
      `Update roles for ${user.firstName} ${user.lastName}`,
      async () => {
        try {
          await userService.updateUserRoles(user.id, formData.roles);
          showNotification('User roles updated successfully', 'success');
          fetchUsers();
        } catch (error) {
          console.error('Failed to update roles:', error);
          const errorMessage = getErrorMessage(error, 'Failed to update roles. Please try again.');
          showNotification(errorMessage, 'error');
        }
      },
      {
        type: 'info',
        confirmText: 'Update Roles',
      }
    );
  };

  const getStatusColor = (status: UserStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'SUSPENDED':
        return 'error';
      case 'PENDING':
        return 'warning';
      case 'INACTIVE':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'ADMIN':
        return <AdminPanelSettings fontSize="small" />;
      case 'OPERATIONS':
      case 'FINANCE':
      case 'COMPLIANCE':
        return <Badge fontSize="small" />;
      default:
        return <Person fontSize="small" />;
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchUsers();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRole = (role: UserRole) => {
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">User Management</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Sync />}
            onClick={handleSyncFromKeycloak}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync from Keycloak'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUsers}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateUser}
          >
            Create User
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4">{statistics?.totalUsers ?? totalElements}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Person />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Users
                  </Typography>
                  <Typography variant="h4">
                    {statistics?.activeUsers ?? 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Suspended Users
                  </Typography>
                  <Typography variant="h4">
                    {statistics?.suspendedUsers ?? 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <PersonOff />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending Users
                  </Typography>
                  <Typography variant="h4">
                    {statistics?.pendingUsers ?? 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <HourglassEmpty />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={filterRole}
                label="Role"
                onChange={(e) => setFilterRole(e.target.value as UserRole | '')}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="OPERATIONS">Operations</MenuItem>
                <MenuItem value="FINANCE">Finance</MenuItem>
                <MenuItem value="COMPLIANCE">Compliance</MenuItem>
                <MenuItem value="MERCHANT">Merchant</MenuItem>
                <MenuItem value="MERCHANT_ADMIN">Merchant Admin</MenuItem>
                <MenuItem value="RETAILER">Retailer</MenuItem>
                <MenuItem value="RETAILER_ADMIN">Retailer Admin</MenuItem>
                <MenuItem value="AGENT">Agent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value as UserStatus | '')}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={handleSearch}>
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      {loading ? (
        <LinearProgress />
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          {user.firstName[0]}
                          {user.lastName[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {user.email}
                        {user.emailVerified && (
                          <Tooltip title="Email verified">
                            <VerifiedUser fontSize="small" color="success" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {user.roles.map((role) => (
                          <Chip
                            key={role}
                            label={role}
                            size="small"
                            icon={getRoleIcon(role)}
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={getStatusColor(user.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.partnerName ? (
                        <Box>
                          <Typography variant="body2">{user.partnerName}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {user.partnerType}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.lastLogin ? (
                        <Typography variant="caption">
                          {format(new Date(user.lastLogin), 'MMM dd, yyyy HH:mm')}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Never
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditUser(user)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password">
                        <IconButton
                          size="small"
                          onClick={() => handleResetPassword(user)}
                        >
                          <LockReset fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}>
                        <IconButton
                          size="small"
                          color={user.status === 'ACTIVE' ? 'warning' : 'success'}
                          onClick={() => handleSuspendUser(user)}
                        >
                          {user.status === 'ACTIVE' ? (
                            <Block fontSize="small" />
                          ) : (
                            <CheckCircle fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUser(user)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit User' : 'Create New User'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                placeholder="+263712345678"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={partners}
                getOptionLabel={(option) => `${option.partnerCode} — ${option.partnerName} (${option.partnerType})`}
                value={partners.find(p => p.partnerCode === formData.partnerCode) || null}
                onChange={(_, newValue) => setFormData({ ...formData, partnerCode: newValue?.partnerCode || '' })}
                isOptionEqualToValue={(option, value) => option.partnerCode === value.partnerCode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={formData.roles.some(r => PARTNER_ROLES.includes(r)) ? 'Partner' : 'Partner (Optional)'}
                    required={formData.roles.some(r => PARTNER_ROLES.includes(r))}
                    error={formData.roles.some(r => PARTNER_ROLES.includes(r)) && !formData.partnerCode}
                    helperText={formData.roles.some(r => PARTNER_ROLES.includes(r)) && !formData.partnerCode ? 'Required for partner roles' : ''}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider>
                <Typography variant="caption">User Roles</Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {(['ADMIN', 'OPERATIONS', 'FINANCE', 'COMPLIANCE', 'MERCHANT', 'MERCHANT_ADMIN', 'RETAILER', 'RETAILER_ADMIN', 'AGENT'] as UserRole[]).map((role) => {
                  const hasMerchant = formData.roles.some(r => r === 'MERCHANT' || r === 'MERCHANT_ADMIN');
                  const hasRetailer = formData.roles.some(r => r === 'RETAILER' || r === 'RETAILER_ADMIN');
                  const isMerchantRole = role === 'MERCHANT' || role === 'MERCHANT_ADMIN';
                  const isRetailerRole = role === 'RETAILER' || role === 'RETAILER_ADMIN';
                  const disabled = (isMerchantRole && hasRetailer) || (isRetailerRole && hasMerchant);

                  return (
                    <FormControlLabel
                      key={role}
                      control={
                        <Checkbox
                          checked={formData.roles.includes(role)}
                          onChange={() => toggleRole(role)}
                          disabled={disabled}
                        />
                      }
                      label={role}
                    />
                  );
                })}
              </Box>
            </Grid>

            {!editMode && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.sendWelcomeEmail}
                      onChange={(e) =>
                        setFormData({ ...formData, sendWelcomeEmail: e.target.checked })
                      }
                    />
                  }
                  label="Send welcome email with login credentials"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveUser}
            disabled={
              !formData.username ||
              !formData.email ||
              !formData.firstName ||
              !formData.lastName ||
              formData.roles.length === 0 ||
              (formData.roles.some(r => PARTNER_ROLES.includes(r)) && !formData.partnerCode)
            }
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        inputLabel={confirmDialog.inputLabel}
        inputPlaceholder={confirmDialog.inputPlaceholder}
        inputRequired={confirmDialog.inputRequired}
        onConfirm={(inputValue) => {
          confirmDialog.onConfirm(inputValue);
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
      />

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </Box>
  );
};

export default UserManagement;
